import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend,
} from 'recharts';
import {
  Users, UserCheck, UserPlus, CalendarCheck, UserMinus, Wallet, Clock3,
  Landmark, RefreshCw, AlertCircle, ArrowRight, ChevronRight, PartyPopper,
  ShieldAlert, Building2, TrendingUp, Loader2,
} from 'lucide-react';

import { StatGrid, ChartCard } from '../components/dashboard';

import employeeService from '../services/EmployeeService';
import leaveService from '../services/LeaveService';
import holidayService from '../services/holidayService';
import payRollService from '../services/PayrollService';
import { getEmployeesAttendanceRows } from '../store/slices/attendanceSummarySlice';

import {
  LEAVE_STATUSES, monthBounds, isoDate, parseDate,
  fmtCompactRs, fmtRs, fmtHours,
  computeHeadcount, countPendingBankAccounts, buildHeadcountByDepartment,
  buildHiringTrend, employeeName, employeeServiceNo,
  attendanceKpis, buildAttendanceTrend,
  leaveKpis, buildLeaveStatusDonut, leaveTypeMap, buildLeaveQueue,
  upcomingHolidays,
  overtimeKpis, buildOvertimeTrend,
  payrollKpis, buildSalaryComposition, buildTopSalaries, currentPeriod,
  loanKpis,
} from '../utils/mainDashboardAnalytics';

/* Every feed answers { StatusCode, ResultSet }, but some services hand back the
   axios response and others the parsed body. One unwrapper covers both. */
const rowsOf = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  const body = res.data !== undefined && res.data !== null ? res.data : res;
  const set = body.ResultSet ?? body.Result ?? body.Data ?? null;
  if (Array.isArray(set)) return set;
  return set ? [set] : [];
};

/* A feed that fails must not take the page down with it — each one resolves to
   an empty array and its own error label instead. */
const settle = async (label, fn) => {
  try {
    return { label, rows: rowsOf(await fn()), error: null };
  } catch (err) {
    console.error(`[Dashboard] ${label} failed:`, err);
    return { label, rows: [], error: err?.message || 'Request failed' };
  }
};

const tooltipStyle = { borderRadius: 8, fontSize: 12, border: '1px solid #e5e7eb' };
const axisTick = { fontSize: 11, fill: '#9ca3af' };
const CARD = 'bg-white border border-gray-100 rounded-xl shadow-sm';

const authToken = () =>
  localStorage.getItem('auth-key') || localStorage.getItem('AuthKey') || '';

const showDate = (v) => {
  const d = parseDate(v);
  return d ? isoDate(d) : '—';
};

/* ── Small building blocks ───────────────────────────────────────────────── */

function SectionTitle({ children, action }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3 mt-6">
      <h2 className="text-[13px] font-bold text-gray-700 tracking-wide">{children}</h2>
      {action}
    </div>
  );
}

function ListCard({ title, subtitle, icon: Icon, children, empty, emptyText, footer }) {
  return (
    <div className={`${CARD} flex flex-col h-full`}>
      <div className="px-5 py-4 border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon size={14} className="text-primary-600 shrink-0" />}
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        </div>
        {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {empty ? (
        <div className="flex-1 flex items-center justify-center py-10">
          <p className="text-[12px] text-gray-400">{emptyText}</p>
        </div>
      ) : (
        <div className="flex-1">{children}</div>
      )}
      {footer && <div className="px-5 py-3 border-t border-gray-100 shrink-0">{footer}</div>}
    </div>
  );
}

/* Full class strings only — Tailwind's JIT can't see assembled names. */
const ACTION_TONES = {
  amber:  'bg-amber-50 text-amber-700 group-hover:bg-amber-600 group-hover:text-white',
  blue:   'bg-blue-50 text-blue-700 group-hover:bg-blue-600 group-hover:text-white',
  violet: 'bg-violet-50 text-violet-700 group-hover:bg-violet-600 group-hover:text-white',
};

/* ── Page ────────────────────────────────────────────────────────────────── */

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth || {});

  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState([]);
  const [data, setData] = useState({
    employees: [], attendance: [], leaveByStatus: {}, leaveTypes: [],
    holidays: [], overtime: [], periods: [], salaries: [], loans: [],
  });

  const today = useMemo(() => new Date(), []);
  const { fromDate, toDate } = useMemo(() => monthBounds(today), [today]);

  const load = useCallback(async () => {
    setLoading(true);

    const leaveCalls = LEAVE_STATUSES.map((status) =>
      settle(`Leave (${status})`, () => leaveService.GetLeavesRequestByStatus(status))
    );

    const [
      employees, attendance, leaveTypes, holidays,
      overtime, periods, salaries, loans, ...leaveResults
    ] = await Promise.all([
      settle('Employees',       () => employeeService.GetEmployeeList()),
      settle('Attendance',      () => getEmployeesAttendanceRows({ fromDate, toDate })),
      settle('Leave types',     () => leaveService.GetLeaveTypeList()),
      settle('Holidays',        () => holidayService.getAllHolidays(authToken())),
      settle('Overtime',        () => payRollService.GetOvertimeList()),
      settle('Payroll periods', () => payRollService.GetPayRollPeriodList()),
      settle('Employee salary', () => payRollService.GetEmployeeSalaryList()),
      settle('Loans',           () => payRollService.GetLoanList()),
      ...leaveCalls,
    ]);

    const leaveByStatus = {};
    LEAVE_STATUSES.forEach((status, i) => { leaveByStatus[status] = leaveResults[i].rows; });

    setData({
      employees: employees.rows,
      attendance: attendance.rows,
      leaveByStatus,
      leaveTypes: leaveTypes.rows,
      holidays: holidays.rows,
      overtime: overtime.rows,
      periods: periods.rows,
      salaries: salaries.rows,
      loans: loans.rows,
    });

    setFailed(
      [employees, attendance, leaveTypes, holidays, overtime, periods, salaries, loans, ...leaveResults]
        .filter((r) => r.error)
        .map((r) => r.label)
    );
    setLoading(false);
  }, [fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  /* ── Derived, every figure from the feeds above ────────────────────────── */
  const headcount    = useMemo(() => computeHeadcount(data.employees), [data.employees]);
  const pendingBanks = useMemo(() => countPendingBankAccounts(data.employees), [data.employees]);
  const byDept       = useMemo(() => buildHeadcountByDepartment(data.employees), [data.employees]);
  const hiring       = useMemo(() => buildHiringTrend(data.employees, 12, today), [data.employees, today]);
  const att          = useMemo(() => attendanceKpis(data.attendance, headcount.active, today), [data.attendance, headcount.active, today]);
  const attTrend     = useMemo(() => buildAttendanceTrend(data.attendance, fromDate, toDate), [data.attendance, fromDate, toDate]);
  const leave        = useMemo(() => leaveKpis(data.leaveByStatus, today), [data.leaveByStatus, today]);
  const leaveDonut   = useMemo(() => buildLeaveStatusDonut(data.leaveByStatus), [data.leaveByStatus]);
  const typeMap      = useMemo(() => leaveTypeMap(data.leaveTypes), [data.leaveTypes]);
  const holidayList  = useMemo(() => upcomingHolidays(data.holidays, today), [data.holidays, today]);
  const ot           = useMemo(() => overtimeKpis(data.overtime), [data.overtime]);
  const otTrend      = useMemo(() => buildOvertimeTrend(data.overtime, 6, today), [data.overtime, today]);
  const pay          = useMemo(() => payrollKpis(data.salaries), [data.salaries]);
  const salarySplit  = useMemo(() => buildSalaryComposition(pay), [pay]);
  const topSalaries  = useMemo(() => buildTopSalaries(data.salaries), [data.salaries]);
  const period       = useMemo(() => currentPeriod(data.periods), [data.periods]);
  const loans        = useMemo(() => loanKpis(data.loans), [data.loans]);

  const nameBySvcNo = useMemo(() => {
    const m = {};
    data.employees.forEach((e) => { m[employeeServiceNo(e)] = employeeName(e); });
    return m;
  }, [data.employees]);

  const leaveQueue = useMemo(
    () => buildLeaveQueue(data.leaveByStatus, typeMap, nameBySvcNo),
    [data.leaveByStatus, typeMap, nameBySvcNo]
  );

  /* Only items with real work in them reach the panel. */
  const actions = useMemo(() => ([
    { key: 'emp',   label: 'Employee approvals',     count: headcount.pendingApproval, to: '/hrm/employees',        tone: 'amber'  },
    { key: 'bank',  label: 'Bank account approvals', count: pendingBanks,              to: '/hrm/employees',        tone: 'amber'  },
    { key: 'leave', label: 'Leave requests',         count: leave.pending,             to: '/hrm/leave/approval',   tone: 'blue'   },
    { key: 'ot',    label: 'Overtime approvals',     count: ot.pendingCount,           to: '/hrm/overtime/details', tone: 'violet' },
  ].filter((a) => a.count > 0)), [headcount.pendingApproval, pendingBanks, leave.pending, ot.pendingCount]);

  const workforceStats = [
    { label: 'Total Employees',   value: headcount.total.toLocaleString(),   sub: `${headcount.departments} departments`,             icon: Users,         tone: 'primary' },
    { label: 'Active Staff',      value: headcount.active.toLocaleString(),  sub: `${headcount.activeRate.toFixed(0)}% of headcount`, icon: UserCheck,     tone: 'emerald' },
    { label: 'Present Today',     value: att.presentToday.toLocaleString(),  sub: `${att.attendanceRate.toFixed(0)}% avg attendance`, icon: CalendarCheck, tone: 'blue'    },
    { label: 'On Leave Today',    value: leave.onLeaveToday.toLocaleString(), sub: `${leave.pending} awaiting approval`,              icon: UserMinus,     tone: 'amber'   },
    { label: 'Pending Approvals', value: (headcount.pendingApproval + pendingBanks).toLocaleString(), sub: `${headcount.pendingApproval} staff · ${pendingBanks} bank`, icon: ShieldAlert, tone: 'orange' },
    { label: 'New Hires',         value: (hiring[hiring.length - 1]?.hires ?? 0).toLocaleString(),    sub: 'This month',              icon: UserPlus,      tone: 'violet'  },
  ];

  const payrollStats = [
    { label: 'Monthly Gross',  value: fmtCompactRs(pay.monthlyGross),    sub: `${pay.employeesOnPayroll} on payroll`, icon: Wallet,     tone: 'primary' },
    { label: 'Basic Salary',   value: fmtCompactRs(pay.monthlyBasic),    sub: 'Excludes allowances',                  icon: Landmark,   tone: 'emerald' },
    { label: 'Average Gross',  value: fmtCompactRs(pay.avgGross),        sub: 'Per employee',                         icon: TrendingUp, tone: 'blue'    },
    { label: 'Overtime Cost',  value: fmtCompactRs(ot.totalAmount),      sub: `${fmtHours(ot.pendingHours)} pending`, icon: Clock3,     tone: 'indigo'  },
    { label: 'Loan Balance',   value: fmtCompactRs(loans.outstanding),   sub: `${loans.activeLoans} active loans`,    icon: Landmark,   tone: 'amber'   },
    { label: 'Loan Recovery',  value: fmtCompactRs(loans.monthlyRecovery), sub: 'Per month',                          icon: Wallet,     tone: 'sky'     },
  ];

  const monthLabel = today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-32 text-sm text-gray-400">
        <Loader2 size={16} className="animate-spin" /> Loading dashboard...
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Welcome back{user?.name ? `, ${String(user.name).split(' ')[0]}` : ''}
          </h1>
          <p className="text-[13px] text-gray-500 mt-0.5">
            Workforce and payroll position for {monthLabel}.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 bg-white text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* A feed that failed is named, so a zero is never mistaken for a fact. */}
      {failed.length > 0 && (
        <div className="flex items-start gap-2 mb-5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
          <AlertCircle size={15} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-[12px] text-amber-800">
            <span className="font-semibold">Some data could not be loaded:</span>{' '}
            {failed.join(', ')}. Figures below exclude these sources.
          </p>
        </div>
      )}

      {/* ── Workforce ── */}
      <div className="mb-3">
        <h2 className="text-[13px] font-bold text-gray-700 tracking-wide">Workforce</h2>
      </div>
      <StatGrid stats={workforceStats} columns={6} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <ChartCard
          className="lg:col-span-2"
          title="Attendance This Month"
          subtitle={`Employees present per day · ${fromDate} to ${toDate}`}
          icon={CalendarCheck}
          empty={!attTrend.some((d) => d.present > 0)}
          emptyText="No attendance recorded this month"
        >
          <ResponsiveContainer width="100%" height={230}>
            <AreaChart data={attTrend}>
              <defs>
                <linearGradient id="attFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} interval={1} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v) => [`${v} present`, '']}
                labelFormatter={(l, p) => (p && p[0] && p[0].payload ? p[0].payload.day : l)}
              />
              <Area type="monotone" dataKey="present" stroke="#2563eb" strokeWidth={2} fill="url(#attFill)" name="Present" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Headcount by Department"
          subtitle="Active employees"
          icon={Building2}
          empty={!byDept.length}
          emptyText="No department data"
        >
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={byDept} layout="vertical" barSize={13}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false} width={110} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" name="Employees" fill="#047857" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Payroll ── */}
      <SectionTitle
        action={
          <button
            onClick={() => navigate('/payroll/dashboard')}
            className="flex items-center gap-1 text-[11px] font-semibold text-primary-700 hover:text-primary-800"
          >
            Open Payroll <ArrowRight size={12} />
          </button>
        }
      >
        Payroll &amp; Cost
      </SectionTitle>
      <StatGrid stats={payrollStats} columns={6} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <ChartCard
          title="Salary Composition"
          subtitle="Current monthly gross"
          icon={Wallet}
          empty={!salarySplit.length}
          emptyText="No salary records"
        >
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie data={salarySplit} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={2}>
                {salarySplit.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtRs(v)} />
              <Legend verticalAlign="bottom" height={24} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          className="lg:col-span-2"
          title="Overtime Trend"
          subtitle="Hours and cost per month"
          icon={Clock3}
          empty={!otTrend.some((d) => d.hours > 0)}
          emptyText="No overtime recorded"
        >
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={otTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis yAxisId="h" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis
                yAxisId="a" orientation="right" tick={axisTick} axisLine={false} tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)}
              />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => (n === 'Cost' ? fmtRs(v) : `${v} hrs`)} />
              <Legend verticalAlign="top" height={24} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="h" dataKey="hours"  name="Hours" fill="#4f46e5" radius={[4, 4, 0, 0]} barSize={18} />
              <Bar yAxisId="a" dataKey="amount" name="Cost"  fill="#0891b2" radius={[4, 4, 0, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── What needs a decision ── */}
      <SectionTitle>Needs Attention</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ListCard
          title="Action Centre"
          subtitle="Items waiting on a decision"
          icon={ShieldAlert}
          empty={!actions.length}
          emptyText="Nothing is waiting for approval"
        >
          <div className="divide-y divide-gray-50">
            {actions.map((a) => (
              <button
                key={a.key}
                onClick={() => navigate(a.to)}
                className="group w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50 transition-colors text-left"
              >
                <span className="text-[13px] font-medium text-gray-700">{a.label}</span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full text-[12px] font-bold tabular-nums transition-colors ${ACTION_TONES[a.tone]}`}>
                    {a.count}
                  </span>
                  <ChevronRight size={14} className="text-gray-300 group-hover:text-gray-500" />
                </span>
              </button>
            ))}
          </div>
        </ListCard>

        <ListCard
          title="Leave Queue"
          subtitle="Soonest start date first"
          icon={UserMinus}
          empty={!leaveQueue.length}
          emptyText="No pending leave requests"
          footer={
            <button
              onClick={() => navigate('/hrm/leave/approval')}
              className="flex items-center gap-1 text-[11px] font-semibold text-primary-700 hover:text-primary-800"
            >
              Review all {leave.pending} requests <ArrowRight size={12} />
            </button>
          }
        >
          <div className="divide-y divide-gray-50">
            {leaveQueue.map((r) => (
              <div key={r.formNo || `${r.serviceNo}-${r.from}`} className="px-5 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[12px] font-semibold text-gray-800 truncate">{r.name}</p>
                  <p className="text-[11px] text-gray-400 truncate">{r.type} · from {showDate(r.from)}</p>
                </div>
                <span className="text-[11px] font-semibold text-gray-500 shrink-0 tabular-nums">
                  {r.days} {r.days === 1 ? 'day' : 'days'}
                </span>
              </div>
            ))}
          </div>
        </ListCard>

        <ListCard
          title="Upcoming Holidays"
          subtitle="From today onwards"
          icon={PartyPopper}
          empty={!holidayList.length}
          emptyText="No upcoming holidays"
        >
          <div className="divide-y divide-gray-50">
            {holidayList.map((h) => (
              <div key={h.id || h.name} className="px-5 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[12px] font-semibold text-gray-800 truncate">{h.name}</p>
                  <p className="text-[11px] text-gray-400 truncate">
                    {showDate(h.date)}{h.type ? ` · ${h.type}` : ''}
                  </p>
                </div>
                <span className="text-[11px] font-semibold text-gray-500 shrink-0 whitespace-nowrap">
                  {h.daysAway === 0 ? 'Today' : `in ${h.daysAway}d`}
                </span>
              </div>
            ))}
          </div>
        </ListCard>
      </div>

      {/* ── Period status · hiring · leave mix ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <ListCard
          title="Current Payroll Period"
          subtitle={period ? period.name : undefined}
          icon={Wallet}
          empty={!period}
          emptyText="No payroll period defined"
        >
          {period && (
            <div className="px-5 py-4 space-y-2.5">
              {[
                ['Status', period.status],
                ['Period', `${showDate(period.start)} → ${showDate(period.end)}`],
                ['Payment date', showDate(period.paymentDate)],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <span className="text-[11px] text-gray-500">{k}</span>
                  <span className="text-[12px] font-semibold text-gray-800 text-right">{v}</span>
                </div>
              ))}
              <div className="flex flex-wrap gap-1.5 pt-1.5">
                {[
                  ['Locked', period.locked],
                  ['GL posted', period.glPosted],
                  ['Bank file', period.bankGenerated],
                ].map(([label, done]) => (
                  <span
                    key={label}
                    className={`text-[10px] font-semibold px-2 py-1 rounded-full ${
                      done ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {done ? '✓ ' : '· '}{label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </ListCard>

        <ChartCard
          title="Hiring Trend"
          subtitle="New employees per month"
          icon={UserPlus}
          empty={!hiring.some((d) => d.hires > 0)}
          emptyText="No joining dates on record"
          emptyHeight={190}
        >
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={hiring}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval={0} angle={-35} textAnchor="end" height={42} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="hires" name="Hires" fill="#7c3aed" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Leave by Status"
          subtitle="All requests on record"
          icon={UserMinus}
          empty={!leaveDonut.length}
          emptyText="No leave requests"
          emptyHeight={190}
        >
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie data={leaveDonut} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={2}>
                {leaveDonut.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend verticalAlign="bottom" height={24} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Where the salary bill sits ── */}
      {topSalaries.length > 0 && (
        <>
          <SectionTitle
            action={
              <button
                onClick={() => navigate('/payroll/employee-salary')}
                className="flex items-center gap-1 text-[11px] font-semibold text-primary-700 hover:text-primary-800"
              >
                Employee salaries <ArrowRight size={12} />
              </button>
            }
          >
            Highest Salary Cost
          </SectionTitle>
          <div className={`${CARD} overflow-hidden`}>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['Employee', 'Service No', 'Basic', 'Gross'].map((h, i) => (
                    <th
                      key={h}
                      className={`px-4 py-3 text-[12px] font-semibold text-gray-500 whitespace-nowrap ${i > 1 ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topSalaries.map((r) => (
                  <tr key={r.id || r.serviceNo} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                    <td className="px-4 py-3 font-mono text-gray-600">{r.serviceNo || '—'}</td>
                    <td className="px-4 py-3 text-right text-gray-600 tabular-nums">{fmtRs(r.basic)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 tabular-nums">{fmtRs(r.gross)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}